<!-- eslint-disable -->
<template>
  <div class="crm-dashboard">
    <!-- ===== Header ===== -->
    <div class="dash-header">
      <div>
        <h2 class="dash-title">Sales Dashboard</h2>
        <p class="dash-sub">Live overview of the CRM pipeline</p>
      </div>
      <!--
        PERIOD FILTER — Karim, 17 Aug: "in dashboard i cannot filter by month."
        He was right: this page called crm/dashboard with NO parameters at all, so it
        always showed the last 30 days and there was no control to change it. The
        backend already accepted ?from=&to=; only the UI was missing.
      -->
      <div class="dash-period">
        <!-- Karim, 27 Sep: "no need for last 30 or 90 days, we need the month
             directly." A plain <select> — Safari has no <input type="month">, it
             rendered as an empty text box, which is why picking a month "did not work". -->
        <button type="button" class="btn-refresh" title="Previous month" :disabled="loading" @click="shiftMonth(-1)">
          <feather-icon icon="ChevronLeftIcon" size="16" />
        </button>
        <select v-model="pickedMonth" class="dash-period__select" @change="fetchDashboard">
          <option v-for="m in monthOptions" :key="m.value" :value="m.value">{{ m.label }}</option>
        </select>
        <button type="button" class="btn-refresh" title="Next month" :disabled="loading || pickedMonth >= monthOptions[0].value" @click="shiftMonth(1)">
          <feather-icon icon="ChevronRightIcon" size="16" />
        </button>

        <!-- Karim, 2 Sep: "branches will need separate data... dashboards,
             comparison, analysis." Manager/admin only — narrows this whole page
             to one branch; the By branch card further down always compares
             every branch regardless of this filter. -->
        <select
          v-if="access.canViewTeam && officeRows.length"
          v-model="officeFilter"
          class="dash-period__select"
          @change="fetchDashboard"
        >
          <option value="">All branches</option>
          <option v-for="o in officeRows.filter(r => r.officeId)" :key="o.officeId" :value="o.officeId">
            {{ o.officeName }}
          </option>
        </select>

        <button class="btn-refresh" :disabled="loading" @click="fetchDashboard">
          <feather-icon icon="RefreshCwIcon" size="16" :class="{ spinning: loading }" />
          <span>Refresh</span>
        </button>
      </div>
    </div>

    <!--
      Say exactly WHICH numbers the period governs. Activity and issued certificates
      are things that happened during a window; the pipeline is a snapshot of what is
      open right now and has no period. Blurring the two would have people reading a
      month-filtered pipeline that was never filtered.
    -->
    <p v-if="activeRange" class="dash-range">
      Activity and certificates: <strong>{{ activeRange }}</strong>.
      Pipeline figures below are current, not period-based.
    </p>

    <!-- ===== Error ===== -->
    <div v-if="error" class="alert-error">
      <feather-icon icon="AlertTriangleIcon" size="16" />
      <span>{{ error }}</span>
    </div>

    <!-- D-c2: say out loud what these numbers are, and what they exclude.
         Previously SAR was added to AED and the total was labelled "AED". -->
    <div v-for="(w, i) in currencyWarnings" :key="'cw-' + i" class="alert-error alert-error--warn">
      <feather-icon icon="AlertTriangleIcon" size="16" />
      <span>{{ w }}</span>
    </div>

    <!-- ===== Loading ===== -->
    <div v-if="loading" class="loading-wrap">
      <div class="spinner"></div>
      <span class="loading-text">Loading dashboard…</span>
    </div>

    <template v-else>
      <!-- ===== KPI ROW ===== -->
      <div class="kpi-grid">
        <div class="card kpi-card">
          <div class="kpi-icon" style="background: rgba(37, 99, 235, 0.12); color: #2563eb;">
            <feather-icon icon="TargetIcon" size="16" />
          </div>
          <div class="kpi-body">
            <div class="kpi-value">{{ formatNumber(totals.openOpportunities) }}</div>
            <div class="kpi-label">Open opportunities</div>
          </div>
        </div>

        <div class="card kpi-card">
          <div class="kpi-icon" style="background: rgba(0, 207, 232, 0.12); color: #00cfe8;">
            <feather-icon icon="TrendingUpIcon" size="16" />
          </div>
          <div class="kpi-body">
            <div class="kpi-value">{{ formatCurrency(totals.pipelineValue) }}</div>
            <div class="kpi-label">Pipeline value</div>
            <!-- Karim, 27 Sep ("feels fake"): most opportunities carry no value yet — say so. -->
            <div v-if="totals.openOpportunities" class="kpi-note">value set on {{ formatNumber(totals.openWithValue) }} of {{ formatNumber(totals.openOpportunities) }}</div>
          </div>
        </div>

        <div class="card kpi-card">
          <div class="kpi-icon" style="background: rgba(40, 199, 111, 0.12); color: #28c76f;">
            <feather-icon icon="AwardIcon" size="16" />
          </div>
          <div class="kpi-body">
            <div class="kpi-value">{{ formatNumber(totals.won) }}</div>
            <div class="kpi-label">Won</div>
          </div>
        </div>

        <div class="card kpi-card">
          <div class="kpi-icon" style="background: rgba(40, 199, 111, 0.12); color: #28c76f;">
            <feather-icon icon="DollarSignIcon" size="16" />
          </div>
          <div class="kpi-body">
            <div class="kpi-value">{{ formatCurrency(totals.wonValue) }}</div>
            <div class="kpi-label">Won value</div>
            <div v-if="totals.won" class="kpi-note">value set on {{ formatNumber(totals.wonWithValue) }} of {{ formatNumber(totals.won) }}</div>
          </div>
        </div>

        <div class="card kpi-card">
          <div class="kpi-icon" style="background: rgba(59, 130, 246, 0.12); color: #3b82f6;">
            <feather-icon icon="PercentIcon" size="16" />
          </div>
          <div class="kpi-body">
            <div class="kpi-value">{{ formatPercent(totals.winRate) }}</div>
            <div class="kpi-label">Win rate</div>
          </div>
        </div>

        <div class="card kpi-card">
          <div class="kpi-icon" style="background: rgba(234, 84, 85, 0.12); color: #ea5455;">
            <feather-icon icon="UserPlusIcon" size="16" />
          </div>
          <div class="kpi-body">
            <div class="kpi-value">{{ formatNumber(totals.openLeads) }}</div>
            <div class="kpi-label">Open leads</div>
          </div>
        </div>
      </div>

      <!-- ===== MID GRID: Funnel + Activities ===== -->
      <div class="grid-2">
        <!-- Funnel -->
        <div class="card">
          <div class="card-head">
            <h4 class="card-title">
              <feather-icon icon="FilterIcon" size="16" />
              <span>Pipeline funnel</span>
            </h4>
            <span class="card-meta">{{ funnel.length }} stages</span>
          </div>

          <div v-if="!funnel.length" class="empty-state">
            <feather-icon icon="InboxIcon" size="16" />
            <span>No pipeline data yet</span>
          </div>

          <div v-else class="funnel-list">
            <div v-for="(row, i) in funnel" :key="'fn-' + i" class="funnel-row">
              <div class="funnel-top">
                <span class="funnel-name">{{ row.name || row.stage || '—' }}</span>
                <span class="funnel-nums">
                  <span class="funnel-count">{{ formatNumber(row.count) }}</span>
                  <span class="dot-sep">•</span>
                  <span class="funnel-value">{{ formatCurrency(row.value) }}</span>
                </span>
              </div>
              <div class="bar-track">
                <div
                  class="bar-fill"
                  :style="{
                    width: pct(row.count, maxFunnelCount) + '%',
                    backgroundColor: paletteAt(i),
                  }"
                ></div>
              </div>
            </div>
          </div>
        </div>

        <!-- Activities -->
        <div class="card">
          <div class="card-head">
            <h4 class="card-title">
              <feather-icon icon="ClockIcon" size="16" />
              <span>Activities</span>
            </h4>
          </div>

          <div class="activity-list">
            <div class="activity-row is-danger">
              <div class="activity-left">
                <feather-icon icon="AlertCircleIcon" size="16" />
                <span>Overdue</span>
              </div>
              <span class="activity-count">{{ formatNumber(activities.overdue) }}</span>
            </div>
            <div class="activity-row is-warning">
              <div class="activity-left">
                <feather-icon icon="CalendarIcon" size="16" />
                <span>Today</span>
              </div>
              <span class="activity-count">{{ formatNumber(activities.today) }}</span>
            </div>
            <div class="activity-row is-info">
              <div class="activity-left">
                <feather-icon icon="ArrowRightCircleIcon" size="16" />
                <span>Upcoming</span>
              </div>
              <span class="activity-count">{{ formatNumber(activities.upcoming) }}</span>
            </div>
          </div>

          <div class="mini-stats">
            <div class="mini-stat">
              <span class="mini-label">Total leads</span>
              <span class="mini-value">{{ formatNumber(totals.leads) }}</span>
            </div>
            <div class="mini-stat">
              <span class="mini-label">Opportunities</span>
              <span class="mini-value">{{ formatNumber(totals.opportunities) }}</span>
            </div>
            <div class="mini-stat">
              <span class="mini-label">Renewals</span>
              <span class="mini-value">{{ formatNumber(totals.renewals) }}</span>
            </div>
            <div class="mini-stat">
              <span class="mini-label">Quotations</span>
              <span class="mini-value">{{ formatNumber(totals.quotations) }}</span>
            </div>
            <div class="mini-stat">
              <span class="mini-label">Rejected</span>
              <span class="mini-value">{{ formatNumber(totals.rejected) }}</span>
            </div>
          </div>
        </div>
      </div>

      <!-- =====================================================================
           WORK DONE — Airen (12 Aug): reporting must show the daily sales activity,
           not only certified applications. Reads completed activities, which nothing
           displayed before this.
      ====================================================================== -->
      <div class="card">
        <div class="card-head">
          <h4 class="card-title">
            <feather-icon icon="ActivityIcon" size="16" />
            <span>Work done — {{ monthLabel }}</span>
          </h4>
          <span class="card-sub">{{ formatNumber(performance.totalDone) }} activities completed</span>
        </div>

        <div v-if="!performance.totalDone" class="empty">
          <feather-icon icon="ActivityIcon" size="20" />
          <span>No completed activities in this period</span>
          <span class="text-xs text-muted">
            A call, email or visit counts here once it is marked done on the card.
          </span>
        </div>

        <template v-else>
          <!-- What kind of work -->
          <div class="perf-types">
            <div
              v-for="t in performance.byType"
              :key="t.type"
              class="perf-type"
            >
              <span class="perf-type__count">{{ formatNumber(t.count) }}</span>
              <span class="perf-type__label">{{ t.type }}</span>
            </div>
          </div>

          <!-- Who did it -->
          <table class="perf-table">
            <thead>
              <tr>
                <th>Salesperson</th>
                <th
                  v-for="t in performance.byType"
                  :key="'h-' + t.type"
                  class="text-right"
                >
                  {{ t.type }}
                </th>
                <th class="text-right">Total</th>
              </tr>
            </thead>
            <tbody>
              <tr
                v-for="p in performance.byPerson"
                :key="p.userId || p.name"
              >
                <td>{{ p.name }}</td>
                <td
                  v-for="t in performance.byType"
                  :key="p.name + t.type"
                  class="text-right"
                >
                  {{ (p.byType && p.byType[t.type]) || '—' }}
                </td>
                <td class="text-right font-weight-bold">{{ formatNumber(p.total) }}</td>
              </tr>
            </tbody>
          </table>
        </template>
      </div>

      <!--
        LEADS FOR THE MONTH — Karim, 27 Sep: "the leads data brought this month and
        done by month and we add remains leads left for doing the next month."
        Brought = the lead sheet's collected date (not the import date); Done =
        won/rejected with a dated close; Left = brought by month end, still open.
      -->
      <div class="card">
        <div class="card-head">
          <h4 class="card-title">
            <feather-icon icon="UserPlusIcon" size="16" />
            <span>Leads — {{ monthLabel }}</span>
          </h4>
          <span class="card-sub">brought → worked → done · left for next month</span>
        </div>

        <div class="mini-stats leads-month-stats">
          <div class="mini-stat">
            <span class="mini-label">Brought in</span>
            <span class="mini-value">{{ formatNumber(leadsMonth.brought) }}</span>
          </div>
          <div class="mini-stat">
            <span class="mini-label">Worked on</span>
            <span class="mini-value">{{ formatNumber(leadsMonth.worked) }}</span>
          </div>
          <div class="mini-stat">
            <span class="mini-label">Won</span>
            <span class="mini-value text-success">{{ formatNumber(leadsMonth.won) }}</span>
          </div>
          <div class="mini-stat">
            <span class="mini-label">Rejected</span>
            <span class="mini-value text-danger">{{ formatNumber(leadsMonth.rejected) }}</span>
          </div>
          <div class="mini-stat">
            <span class="mini-label">Left for next month</span>
            <span class="mini-value text-primary">{{ formatNumber(leadsMonth.carried) }}</span>
          </div>
        </div>

        <div v-if="!(leadsMonth.byPerson || []).length" class="empty">
          <feather-icon icon="UserPlusIcon" size="20" />
          <span>No leads in this month</span>
        </div>

        <table v-else class="perf-table">
          <thead>
            <tr>
              <th>Salesperson</th>
              <th class="text-right">Brought</th>
              <th class="text-right">Worked</th>
              <th class="text-right">Won</th>
              <th class="text-right">Rejected</th>
              <th class="text-right">Left</th>
            </tr>
          </thead>
          <tbody>
            <tr v-for="l in leadsMonth.byPerson" :key="l.userId || l.name">
              <td>{{ l.name }}</td>
              <td class="text-right">{{ formatNumber(l.brought) }}</td>
              <td class="text-right">{{ formatNumber(l.worked) }}</td>
              <td class="text-right font-weight-bold">{{ formatNumber(l.won) }}</td>
              <td class="text-right">{{ formatNumber(l.rejected) }}</td>
              <td class="text-right">{{ formatNumber(l.carried) }}</td>
            </tr>
          </tbody>
        </table>
        <p v-if="leadsMonth.closedNoDate" class="text-xs text-muted mt-1 mb-0">
          {{ leadsMonth.closedNoDate }} closed lead(s) have no dated close in the lead sheet — not counted in any month.
        </p>
      </div>

      <!-- ===== Certificates credited to each salesperson (Karim, 14 Aug) ===== -->
      <div class="card">
        <div class="card-head">
          <h4 class="card-title">
            <feather-icon icon="AwardIcon" size="16" />
            <span>Certificates from their clients</span>
          </h4>
          <span class="card-sub">
            issued {{ performance.from }} → {{ performance.to }} ·
            {{ formatNumber((performance.certificatesByOwner || {}).total) }} total
          </span>
        </div>

        <!-- Karim, 4 Sep: "count columns is for new clients and customers for
             our existing who issued before" — headline new-vs-existing split,
             scoped by current client ownership so it's never silently empty. -->
        <div class="cert-stat-row">
          <div class="cert-stat">
            <span class="cert-stat-value">{{ formatNumber((performance.certificatesByOwner || {}).total) }}</span>
            <span class="cert-stat-label">Certificates issued</span>
          </div>
          <div class="cert-stat cert-stat-new">
            <span class="cert-stat-value">{{ formatNumber((performance.certificatesByOwner || {}).newTotal) }}</span>
            <span class="cert-stat-label">From new clients</span>
          </div>
          <div class="cert-stat">
            <span class="cert-stat-value">{{ formatNumber((performance.certificatesByOwner || {}).existingTotal) }}</span>
            <span class="cert-stat-label">From existing clients</span>
          </div>
          <div class="cert-stat">
            <span class="cert-stat-value">{{ formatNumber((performance.certificatesByOwner || {}).clients) }}</span>
            <span class="cert-stat-label">Clients ({{ formatNumber((performance.certificatesByOwner || {}).newClients) }} new)</span>
          </div>
        </div>

        <!-- Karim, 27 Sep: it said "924 issued" and "No certificates issued" at once.
             With certificates but no owner rows, say what is actually missing. -->
        <div v-if="!((performance.certificatesByOwner || {}).byOwner || []).length" class="empty">
          <feather-icon icon="AwardIcon" size="20" />
          <span v-if="(performance.certificatesByOwner || {}).total">None of these clients has a salesperson assigned yet</span>
          <span v-else>No certificates issued in this period</span>
        </div>

        <template v-else>
          <table class="perf-table">
            <thead>
              <tr>
                <th>Salesperson</th>
                <th class="text-right">Certificates</th>
                <th class="text-right">New</th>
                <th class="text-right">Existing</th>
                <th class="text-right">Clients</th>
                <th class="text-right">New clients</th>
              </tr>
            </thead>
            <tbody>
              <tr
                v-for="o in performance.certificatesByOwner.byOwner"
                :key="o.userId || o.name"
              >
                <td>{{ o.name }}</td>
                <td class="text-right font-weight-bold">{{ formatNumber(o.certificates) }}</td>
                <td class="text-right">{{ formatNumber(o.newCertificates) }}</td>
                <td class="text-right">{{ formatNumber(o.existingCertificates) }}</td>
                <td class="text-right">{{ formatNumber(o.clients) }}</td>
                <td class="text-right">{{ formatNumber(o.newClients) }}</td>
              </tr>
            </tbody>
          </table>
          <!-- Say plainly what this counts, or it gets read as work done this month. -->
          <p class="perf-note">{{ performance.certificatesByOwner.note }}</p>
        </template>
      </div>

      <!-- ===== By branch ===== Karim, 2 Sep: "branches will need separate data
           to work with... dashboards, comparison, analysis." Manager/admin only
           (the backend already gates it the same way — CrmAccess::canViewTeam);
           always every branch side by side, regardless of the ?officeId= filter
           above, or comparing branches would be pointless. "Unassigned branch" is
           shown, not hidden — it's the honest count of records that don't have a
           branch attached yet (facilities with no ERP application history to
           infer one from). -->
      <div v-if="access.canViewTeam" class="card">
        <div class="card-head">
          <h4 class="card-title">
            <feather-icon icon="GlobeIcon" size="16" />
            <span>By branch</span>
          </h4>
          <span class="card-meta">{{ officeRows.length }} branches</span>
        </div>

        <div v-if="!officeRows.length" class="empty-state">
          <feather-icon icon="GlobeIcon" size="16" />
          <span>No branch data yet</span>
        </div>

        <div v-else class="table-wrap">
          <table class="dash-table">
            <thead>
              <tr>
                <th>Branch</th>
                <th class="ta-right">Deals</th>
                <th class="ta-right">Open</th>
                <th class="ta-right">Won</th>
                <th class="ta-right">Value</th>
                <th class="ta-right">Leads</th>
                <th class="ta-right">Renewals</th>
                <th class="col-rate">Win rate</th>
              </tr>
            </thead>
            <tbody>
              <tr
                v-for="(row, i) in officeRows"
                :key="'off-' + (row.officeId || 'none')"
                :class="{ 'is-unassigned': !row.officeId }"
              >
                <td>
                  <div class="owner-cell">
                    <span class="avatar" :style="{ backgroundColor: paletteAt(i) }">
                      {{ initials(row.officeName) }}
                    </span>
                    <span class="owner-name">{{ row.officeName }}</span>
                  </div>
                </td>
                <td class="ta-right">{{ formatNumber(row.deals) }}</td>
                <td class="ta-right">{{ formatNumber(row.openDeals) }}</td>
                <td class="ta-right">{{ formatNumber(row.won) }}</td>
                <td class="ta-right strong">{{ formatCurrency(row.value) }}</td>
                <td class="ta-right">{{ formatNumber(row.leads) }}</td>
                <td class="ta-right">{{ formatNumber(row.renewals) }}</td>
                <td class="col-rate">
                  <div class="rate-cell">
                    <div class="bar-track sm">
                      <div
                        class="bar-fill"
                        :style="{ width: (row.winRate || 0) + '%', backgroundColor: '#28c76f' }"
                      ></div>
                    </div>
                    <span class="rate-text">{{ row.winRate || 0 }}%</span>
                  </div>
                </td>
              </tr>
            </tbody>
          </table>
        </div>
      </div>

      <!-- ===== By salesperson ===== -->
      <div class="card">
        <div class="card-head">
          <h4 class="card-title">
            <feather-icon icon="UsersIcon" size="16" />
            <span>By salesperson</span>
          </h4>
          <span class="card-meta">{{ sortedSalespeople.length }} owners</span>
        </div>

        <div v-if="!sortedSalespeople.length" class="empty-state">
          <feather-icon icon="UserXIcon" size="16" />
          <span>No salesperson data yet</span>
        </div>

        <div v-else class="table-wrap">
          <table class="dash-table">
            <thead>
              <tr>
                <th>Owner</th>
                <th class="ta-right">Total</th>
                <th class="ta-right">Open</th>
                <th class="ta-right">Won</th>
                <th class="ta-right">Value</th>
                <th class="col-rate">Win rate</th>
              </tr>
            </thead>
            <tbody>
              <tr v-for="(row, i) in sortedSalespeople" :key="'sp-' + i">
                <td>
                  <div class="owner-cell">
                    <span class="avatar" :style="{ backgroundColor: paletteAt(i) }">
                      {{ initials(row.owner) }}
                    </span>
                    <span class="owner-name">{{ row.owner || 'Unassigned' }}</span>
                  </div>
                </td>
                <td class="ta-right">{{ formatNumber(row.total) }}</td>
                <td class="ta-right">{{ formatNumber(row.open) }}</td>
                <td class="ta-right">{{ formatNumber(row.won) }}</td>
                <td class="ta-right strong">{{ formatCurrency(row.value) }}</td>
                <td class="col-rate">
                  <div class="rate-cell">
                    <div class="bar-track sm">
                      <div
                        class="bar-fill"
                        :style="{ width: ownerRate(row) + '%', backgroundColor: '#28c76f' }"
                      ></div>
                    </div>
                    <span class="rate-text">{{ ownerRate(row) }}%</span>
                  </div>
                </td>
              </tr>
            </tbody>
          </table>
        </div>
      </div>

      <!-- ===== Bottom grid: Schemes + Lead sources ===== -->
      <div class="grid-2">
        <!-- Top schemes -->
        <div class="card">
          <div class="card-head">
            <h4 class="card-title">
              <feather-icon icon="LayersIcon" size="16" />
              <span>Top schemes</span>
            </h4>
            <span class="card-meta">Top {{ topSchemes.length }}</span>
          </div>

          <div v-if="!topSchemes.length" class="empty-state">
            <feather-icon icon="InboxIcon" size="16" />
            <span>No scheme data yet</span>
          </div>

          <ul v-else class="rank-list">
            <li v-for="(row, i) in topSchemes" :key="'sc-' + i" class="rank-row">
              <div class="rank-top">
                <span class="rank-name">
                  <span class="rank-index">{{ i + 1 }}</span>
                  {{ row.scheme || '—' }}
                </span>
                <span class="rank-nums">
                  <span class="rank-count">{{ formatNumber(row.count) }}</span>
                  <span class="dot-sep">•</span>
                  <span class="rank-value">{{ formatCurrency(row.value) }}</span>
                </span>
              </div>
              <div class="bar-track">
                <div
                  class="bar-fill"
                  :style="{
                    width: pct(row.count, maxSchemeCount) + '%',
                    backgroundColor: paletteAt(i),
                  }"
                ></div>
              </div>
            </li>
          </ul>
        </div>

        <!-- Lead sources -->
        <div class="card">
          <div class="card-head">
            <h4 class="card-title">
              <feather-icon icon="ShareIcon" size="16" />
              <span>Lead sources</span>
            </h4>
            <span class="card-meta">{{ leadSources.length }} sources</span>
          </div>

          <div v-if="!leadSources.length" class="empty-state">
            <feather-icon icon="InboxIcon" size="16" />
            <span>No lead source data yet</span>
          </div>

          <ul v-else class="rank-list">
            <li v-for="(row, i) in leadSources" :key="'ls-' + i" class="rank-row">
              <div class="rank-top">
                <span class="rank-name">{{ row.source || 'Unknown' }}</span>
                <span class="rank-nums">
                  <span class="rank-count">{{ formatNumber(row.count) }}</span>
                  <span class="rank-share">({{ pct(row.count, totalSourceCount) }}%)</span>
                </span>
              </div>
              <div class="bar-track">
                <div
                  class="bar-fill"
                  :style="{
                    width: pct(row.count, maxSourceCount) + '%',
                    backgroundColor: paletteAt(i + 2),
                  }"
                ></div>
              </div>
            </li>
          </ul>
        </div>
      </div>
    </template>
  </div>
</template>

<script>
/* eslint-disable */
import axios from 'axios'
import decryptData from '@/store/auth/decryptData'

export default {
  name: 'DashboardCRM',
  data() {
    return {
      loading: false,
      error: null,
      data: {},
      // The month shown — "YYYY-MM", defaults to the current month.
      pickedMonth: (() => { const d = new Date(); return `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, '0')}` })(),
      // Karim, 2 Sep: "branches will need separate data... dashboards, comparison,
      // analysis." Narrows the whole page to one branch; the "By branch" card
      // below always compares every branch regardless of this filter.
      officeFilter: '',
      palette: [
        '#2563eb',
        '#00cfe8',
        '#28c76f',
        '#3b82f6',
        '#ea5455',
        '#60a5fa',
        '#1fc5d4',
        '#4b4b4b',
        '#f5a6a6',
        '#6ac96f',
      ],
    }
  },
  computed: {
    /** Current month back 36 months, newest first. */
    monthOptions() {
      const out = []
      const d = new Date()
      d.setDate(1)
      for (let i = 0; i < 36; i++) {
        const value = `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, '0')}`
        out.push({ value, label: d.toLocaleString('en-US', { month: 'long', year: 'numeric' }) })
        d.setMonth(d.getMonth() - 1)
      }
      return out
    },
    monthLabel() {
      const m = this.monthOptions.find(o => o.value === this.pickedMonth)
      return m ? m.label : this.pickedMonth
    },
    /**
     * The window actually reported, taken from the SERVER's answer rather than from
     * what was requested — if the two ever disagree, the screen should show the
     * truth, not the intention.
     */
    activeRange() {
      // The window lives on the `performance` block, which is where the server
      // actually applies it — not at the top level.
      const p = (this.data || {}).performance || {}
      if (!p.from || !p.to) return ''
      return `${p.from} to ${p.to}`
    },

    totals() {
      return (this.data || {}).totals || {}
    },
    access() {
      return (this.data || {}).access || {}
    },
    officeRows() {
      const list = (this.data || {}).byOffice
      return Array.isArray(list) ? list : []
    },
    /** D-c2: the currency every figure on this page was converted into. */
    reportingCurrency() {
      const cur = (this.data || {}).currency || {}
      return cur.reporting || this.totals.valueCurrency || 'AED'
    },
    /** Cards the server could not convert (no rate) — never silently folded in. */
    currencyWarnings() {
      const cur = (this.data || {}).currency || {}
      const list = [...(cur.warnings || [])]
      if (cur.unconvertedCards) {
        list.push(`${cur.unconvertedCards} card(s) in ${(cur.unconvertedCurrencies || []).join(', ') || 'other currencies'} are excluded — no conversion rate available.`)
      }
      return list
    },
    activities() {
      return (this.data || {}).activities || {}
    },
    /** Work actually done — completed activities + lead generation (Airen, 12 Aug). */
    performance() {
      return (this.data || {}).performance || {}
    },
    leadsMonth() {
      return this.performance.leadsMonth || {}
    },
    funnel() {
      const list = (this.data || {}).funnel
      return Array.isArray(list) ? list : []
    },
    salespeople() {
      const list = (this.data || {}).bySalesperson
      return Array.isArray(list) ? list : []
    },
    schemes() {
      const list = (this.data || {}).byScheme
      return Array.isArray(list) ? list : []
    },
    leadSources() {
      const list = (this.data || {}).leadSources
      return Array.isArray(list) ? list : []
    },
    sortedSalespeople() {
      return this.salespeople.slice().sort((a, b) => this.num(b.value) - this.num(a.value))
    },
    topSchemes() {
      return this.schemes
        .slice()
        .sort((a, b) => this.num(b.count) - this.num(a.count))
        .slice(0, 10)
    },
    maxFunnelCount() {
      return this.maxOf(this.funnel, 'count')
    },
    maxSchemeCount() {
      return this.maxOf(this.topSchemes, 'count')
    },
    maxSourceCount() {
      return this.maxOf(this.leadSources, 'count')
    },
    totalSourceCount() {
      return this.leadSources.reduce((sum, r) => sum + this.num(r.count), 0)
    },
  },
  mounted() {
    this.fetchDashboard()
  },
  methods: {
    num(v) {
      const n = Number(v)
      return isFinite(n) ? n : 0
    },
    maxOf(list, key) {
      let max = 0
      ;(list || []).forEach(row => {
        const v = this.num((row || {})[key])
        if (v > max) max = v
      })
      return max
    },
    pct(value, max) {
      const m = this.num(max)
      if (m <= 0) return 0
      const p = (this.num(value) / m) * 100
      return Math.max(0, Math.min(100, Math.round(p)))
    },
    ownerRate(row) {
      const safe = row || {}
      const total = this.num(safe.total)
      if (total <= 0) return 0
      return Math.max(0, Math.min(100, Math.round((this.num(safe.won) / total) * 100)))
    },
    formatNumber(v) {
      return this.num(v).toLocaleString('en-US')
    },
    formatCurrency(v) {
      // D-c2: the server converts every card into ONE reporting currency and
      // says which one. Hardcoding "AED" was wrong the moment ?currency= is used
      // (and dishonest while mixed currencies were being summed).
      return `${this.reportingCurrency} ${Math.round(this.num(v)).toLocaleString('en-US')}`
    },
    formatPercent(v) {
      const n = this.num(v)
      const normalised = n > 0 && n <= 1 ? n * 100 : n
      return (Math.round(normalised * 10) / 10).toLocaleString('en-US') + '%'
    },
    paletteAt(i) {
      const list = this.palette
      return list[((i % list.length) + list.length) % list.length]
    },
    initials(name) {
      const str = (name || '').toString().trim()
      if (!str) return '?'
      const parts = str.split(/\s+/).filter(Boolean)
      if (parts.length === 1) return parts[0].charAt(0).toUpperCase()
      return (parts[0].charAt(0) + parts[1].charAt(0)).toUpperCase()
    },
    shiftMonth(step) {
      const [y, m] = this.pickedMonth.split('-').map(Number)
      const d = new Date(y, m - 1 + step, 1)
      const next = `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, '0')}`
      if (next > this.monthOptions[0].value) return
      this.pickedMonth = next
      this.fetchDashboard()
    },

    /** The picked month as the ?from=&to= the backend reads. */
    periodParams() {
      const [y, m] = this.pickedMonth.split('-').map(Number)
      // Day 0 of the next month is the last day of this one.
      const last = new Date(y, m, 0).getDate()
      return { from: `${this.pickedMonth}-01`, to: `${this.pickedMonth}-${String(last).padStart(2, '0')}` }
    },

    async fetchDashboard() {
      this.loading = true
      this.error = null
      try {
        const params = { ...this.periodParams() }
        if (this.officeFilter) params.officeId = this.officeFilter
        const res = await axios.get('crm/dashboard', { params })
        const body = decryptData(res.data.payload)
        this.data = (body || {}).data || {}
      } catch (e) {
        this.data = {}
        this.error =
          (e && e.response && e.response.data && e.response.data.message) ||
          (e && e.message) ||
          'Failed to load dashboard'
      } finally {
        this.loading = false
      }
    },
  },
}
</script>

<style lang="scss" scoped>
$primary: #2563eb;
$danger: #ea5455;
$warning: #3b82f6;
$info: #00cfe8;
$success: #28c76f;
$body-text: #6e6b7b;
$heading: #5e5873;

.crm-dashboard {
  padding: 0.25rem 0 2rem;
}

/* ---------- Header ---------- */
.dash-header {
  display: flex;
  align-items: center;
  justify-content: space-between;
  flex-wrap: wrap;
  gap: 1rem;
  margin-bottom: 1.25rem;
}

/* Period filter */
.dash-period {
  display: flex;
  align-items: center;
  gap: .5rem;
  flex-wrap: wrap;
}
.dash-period__select,
.dash-period__input {
  border: 1px solid #e4e4ec;
  border-radius: .35rem;
  padding: .4rem .6rem;
  font-size: .82rem;
  background: #fff;
  color: #5e5873;
}
.dash-period__sep { color: #b9b9c3; font-size: .8rem; }
.dash-range {
  font-size: .8rem;
  color: #6e6b7b;
  margin: -0.5rem 0 1rem;
}

.dash-title {
  margin: 0;
  font-size: 1.5rem;
  font-weight: 600;
  color: $heading;
}

.dash-sub {
  margin: 0.15rem 0 0;
  font-size: 0.875rem;
  color: $body-text;
}

.btn-refresh {
  display: inline-flex;
  align-items: center;
  gap: 0.45rem;
  border: none;
  border-radius: 0.358rem;
  padding: 0.6rem 1.1rem;
  background-color: $primary;
  color: #fff;
  font-size: 0.9rem;
  font-weight: 500;
  cursor: pointer;
  transition: box-shadow 0.2s ease, opacity 0.2s ease;

  &:hover:not(:disabled) {
    box-shadow: 0 8px 25px -8px $primary;
  }

  &:disabled {
    opacity: 0.65;
    cursor: not-allowed;
  }
}

.spinning {
  animation: dash-spin 1s linear infinite;
}

/* ---------- Alert ---------- */
/* Work-done panels */
.perf-types {
  display: flex;
  flex-wrap: wrap;
  gap: .5rem;
  margin-bottom: .75rem;
}
.perf-type {
  flex: 1 1 90px;
  background: #f8f8fb;
  border: 1px solid #ececf3;
  border-radius: .5rem;
  padding: .5rem .6rem;
  text-align: center;
}
.perf-type__count { display: block; font-size: 1.15rem; font-weight: 800; color: #2f2f4a; }
.perf-type__label { font-size: .7rem; font-weight: 700; color: #8a8aa3; text-transform: uppercase; letter-spacing: .03em; }

.perf-table {
  width: 100%;
  border-collapse: collapse;
  font-size: .82rem;
}
.perf-table th {
  text-align: left;
  padding: .4rem .5rem;
  color: #8a8aa3;
  font-size: .68rem;
  font-weight: 700;
  text-transform: uppercase;
  letter-spacing: .03em;
  border-bottom: 1px solid #ececf3;
  white-space: nowrap;
}
.perf-table td { padding: .4rem .5rem; border-bottom: 1px solid #f4f4f8; }
.perf-note { margin: .5rem 0 0; font-size: .72rem; color: #8a8aa3; }
.perf-table tbody tr:hover { background: #fafaff; }

.cert-stat-row {
  display: flex;
  flex-wrap: wrap;
  gap: .5rem;
  margin: .25rem 0 .75rem;
}
.cert-stat {
  flex: 1 1 120px;
  background: #f7f7fb;
  border-radius: 8px;
  padding: .5rem .6rem;
  text-align: center;
}
.cert-stat-new { background: #eef7ee; }
.cert-stat-value { display: block; font-size: 1.15rem; font-weight: 800; color: #2f2f4a; }
.cert-stat-label { font-size: .68rem; font-weight: 700; color: #8a8aa3; text-transform: uppercase; letter-spacing: .03em; }

.alert-error--warn {
  background: rgba(59, 130, 246, 0.12) !important;
  color: #1e40af !important;
}

.alert-error {
  display: flex;
  align-items: center;
  gap: 0.5rem;
  padding: 0.85rem 1rem;
  margin-bottom: 1.25rem;
  border-radius: 0.6rem;
  background: rgba(234, 84, 85, 0.12);
  color: $danger;
  font-size: 0.875rem;
}

/* ---------- Loading ---------- */
.loading-wrap {
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  gap: 0.85rem;
  padding: 4rem 1rem;
}

.spinner {
  width: 2.4rem;
  height: 2.4rem;
  border-radius: 50%;
  border: 3px solid rgba(37, 99, 235, 0.2);
  border-top-color: $primary;
  animation: dash-spin 0.8s linear infinite;
}

.loading-text {
  font-size: 0.875rem;
  color: $body-text;
}

@keyframes dash-spin {
  to {
    transform: rotate(360deg);
  }
}

/* ---------- Card ---------- */
.card {
  background: #fff;
  border-radius: 0.6rem;
  box-shadow: 0 2px 8px rgba(34, 41, 47, 0.08);
  padding: 1.25rem;
  margin-bottom: 1.5rem;
}

.card-head {
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: 0.75rem;
  margin-bottom: 1.15rem;
}

.card-title {
  display: flex;
  align-items: center;
  gap: 0.45rem;
  margin: 0;
  font-size: 1.05rem;
  font-weight: 600;
  color: $heading;
}

.card-meta {
  font-size: 0.75rem;
  color: $body-text;
}

/* ---------- KPI ---------- */
.kpi-grid {
  display: grid;
  grid-template-columns: repeat(6, minmax(0, 1fr));
  gap: 1rem;
  margin-bottom: 0.5rem;
}

.kpi-card {
  display: flex;
  align-items: center;
  gap: 0.85rem;
  margin-bottom: 1rem;
  padding: 1.1rem 1rem;
}

.kpi-icon {
  flex: 0 0 auto;
  width: 2.6rem;
  height: 2.6rem;
  border-radius: 50%;
  display: flex;
  align-items: center;
  justify-content: center;
}

.kpi-body {
  min-width: 0;
}

.kpi-value {
  font-size: 1.15rem;
  font-weight: 600;
  color: $heading;
  line-height: 1.25;
  word-break: break-word;
}

.kpi-label {
  font-size: 0.78rem;
  color: $body-text;
  margin-top: 0.1rem;
}

/* ---------- Grid ---------- */
.grid-2 {
  display: grid;
  grid-template-columns: 3fr 2fr;
  gap: 1.5rem;
  align-items: start;
}

/* ---------- Bars ---------- */
.bar-track {
  position: relative;
  width: 100%;
  height: 0.6rem;
  border-radius: 0.4rem;
  background: rgba(34, 41, 47, 0.06);
  overflow: hidden;

  &.sm {
    height: 0.4rem;
  }
}

.bar-fill {
  height: 100%;
  border-radius: 0.4rem;
  transition: width 0.4s ease;
  min-width: 2px;
}

/* ---------- Funnel ---------- */
.funnel-list {
  display: flex;
  flex-direction: column;
  gap: 1rem;
}

.funnel-top,
.rank-top {
  display: flex;
  align-items: baseline;
  justify-content: space-between;
  gap: 0.75rem;
  margin-bottom: 0.4rem;
  font-size: 0.85rem;
}

.funnel-name,
.rank-name {
  font-weight: 500;
  color: $heading;
  overflow: hidden;
  text-overflow: ellipsis;
  white-space: nowrap;
}

.funnel-nums,
.rank-nums {
  flex: 0 0 auto;
  color: $body-text;
  font-size: 0.8rem;
  white-space: nowrap;
}

.funnel-count,
.rank-count {
  font-weight: 600;
  color: $heading;
}

.dot-sep {
  margin: 0 0.35rem;
  opacity: 0.5;
}

.rank-share {
  margin-left: 0.35rem;
  opacity: 0.8;
}

/* ---------- Rank list ---------- */
.rank-list {
  list-style: none;
  margin: 0;
  padding: 0;
  display: flex;
  flex-direction: column;
  gap: 1rem;
}

.rank-index {
  display: inline-block;
  min-width: 1.25rem;
  margin-right: 0.35rem;
  font-size: 0.72rem;
  color: $body-text;
}

/* ---------- Activities ---------- */
.activity-list {
  display: flex;
  flex-direction: column;
  gap: 0.65rem;
}

.activity-row {
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: 0.75rem;
  padding: 0.7rem 0.9rem;
  border-radius: 0.5rem;
  font-size: 0.875rem;
  font-weight: 500;

  &.is-danger {
    background: rgba(234, 84, 85, 0.12);
    color: $danger;
  }

  &.is-warning {
    background: rgba(59, 130, 246, 0.12);
    color: $warning;
  }

  &.is-info {
    background: rgba(0, 207, 232, 0.12);
    color: $info;
  }
}

.activity-left {
  display: flex;
  align-items: center;
  gap: 0.45rem;
}

.activity-count {
  font-size: 1.05rem;
  font-weight: 600;
}

.mini-stats {
  display: grid;
  grid-template-columns: repeat(2, minmax(0, 1fr));
  gap: 0.5rem 1rem;
  margin-top: 1.15rem;
  padding-top: 1rem;
  border-top: 1px solid rgba(34, 41, 47, 0.08);
}

.leads-month-stats {
  grid-template-columns: repeat(5, minmax(0, 1fr));
  margin: 0 0 1rem;
  padding: 0 0 1rem;
  border-top: 0;
  border-bottom: 1px solid rgba(34, 41, 47, 0.08);

  .mini-stat { flex-direction: column; align-items: flex-start; gap: .15rem; }
  .mini-value { font-size: 1.15rem; }
}

.kpi-note { font-size: .68rem; color: #b9b9c3; margin-top: 1px; }

.mini-stat {
  display: flex;
  align-items: center;
  justify-content: space-between;
  font-size: 0.8rem;
}

.mini-label {
  color: $body-text;
}

.mini-value {
  font-weight: 600;
  color: $heading;
}

/* ---------- Table ---------- */
.table-wrap {
  width: 100%;
  overflow-x: auto;
}

.dash-table {
  width: 100%;
  border-collapse: collapse;
  font-size: 0.85rem;

  th {
    text-align: left;
    padding: 0.6rem 0.75rem;
    font-size: 0.72rem;
    font-weight: 600;
    letter-spacing: 0.5px;
    text-transform: uppercase;
    color: $heading;
    background: rgba(34, 41, 47, 0.03);
    white-space: nowrap;
  }

  td {
    padding: 0.7rem 0.75rem;
    color: $body-text;
    border-bottom: 1px solid rgba(34, 41, 47, 0.06);
    white-space: nowrap;
  }

  tbody tr:last-child td {
    border-bottom: none;
  }

  tbody tr:hover td {
    background: rgba(37, 99, 235, 0.04);
  }

  // Records with no branch attached yet — see the By branch card's note.
  tbody tr.is-unassigned td {
    font-style: italic;
    opacity: 0.75;
  }
}

.ta-right {
  text-align: right;
}

.strong {
  font-weight: 600;
  color: $heading;
}

.col-rate {
  min-width: 10rem;
}

.owner-cell {
  display: flex;
  align-items: center;
  gap: 0.55rem;
}

.avatar {
  flex: 0 0 auto;
  width: 2rem;
  height: 2rem;
  border-radius: 50%;
  display: flex;
  align-items: center;
  justify-content: center;
  color: #fff;
  font-size: 0.72rem;
  font-weight: 600;
}

.owner-name {
  font-weight: 500;
  color: $heading;
}

.rate-cell {
  display: flex;
  align-items: center;
  gap: 0.5rem;
}

.rate-text {
  flex: 0 0 auto;
  font-size: 0.75rem;
  font-weight: 600;
  color: $heading;
}

/* ---------- Empty ---------- */
.empty-state {
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  gap: 0.5rem;
  padding: 2.25rem 1rem;
  color: $body-text;
  font-size: 0.85rem;
}

/* ---------- Responsive ---------- */
@media (max-width: 1400px) {
  .kpi-grid {
    grid-template-columns: repeat(3, minmax(0, 1fr));
  }
}

@media (max-width: 992px) {
  .grid-2 {
    grid-template-columns: minmax(0, 1fr);
  }
}

@media (max-width: 576px) {
  .kpi-grid {
    grid-template-columns: repeat(2, minmax(0, 1fr));
  }

  .mini-stats {
    grid-template-columns: minmax(0, 1fr);
  }
  .leads-month-stats {
    grid-template-columns: repeat(2, minmax(0, 1fr));
  }
}
</style>
